import { useDevice } from "@hooks/useDevice";
import { getFeedbackReasonsByType } from "@memorang/helpers/src/feedback";
import type { IconType } from "@memorang/ui";
import * as Clipboard from "expo-clipboard";
import { router } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StyleProp, ViewStyle } from "react-native";
import { IconButton, Menu } from "react-native-paper";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";

type FeedbackType = "positive" | "negative" | "reportIssue";

type FeedbackReasonList = ReturnType<typeof getFeedbackReasonsByType<IconType>>;

/** Derives everything about the dialog that depends on which feedback type was chosen. */
const getDialogPresentation = (
	feedbackType: FeedbackType | undefined,
	t: (key: string) => string,
	negativeReasonList: FeedbackReasonList,
	reportReasonList: FeedbackReasonList,
) => {
	const isReportIssue = feedbackType === "reportIssue";
	const isNegative = feedbackType === "negative";
	const isPositive = feedbackType === "positive";
	return {
		title: isReportIssue ? t("dialog.report.title") : undefined,
		subtitle: isReportIssue
			? t("dialog.report.body")
			: isNegative
				? t("dialog.feedback.negativeBody")
				: isPositive
					? t("dialog.feedback.positiveBody")
					: undefined,
		icon: isReportIssue ? ("flag-outline" as const) : undefined,
		cta: isReportIssue ? t("dialog.report.cta") : undefined,
		rating: isReportIssue ? "negative" : feedbackType,
		type: isReportIssue ? ("Content Flagged" as const) : ("Review" as const),
		feedbackReasonList: isReportIssue
			? reportReasonList
			: isNegative
				? negativeReasonList
				: undefined,
	};
};

type UseAIFeedbackMenuProps = {
	itemId: string | number;
	featureName: "AI Feedback" | "AI Hint" | "AI Coach" | "AI Avatar";
	disabled?: boolean;
	anchorIcon?: "dots-vertical" | "dots-horizontal";
	anchorStyle?: StyleProp<ViewStyle>;
	copyText?: string;
	feedbackDisabled?: boolean;
	threadId?: string;
	feedbackContext?: string;
	responseId?: string;
};

export const AIFeedbackMenu = ({
	itemId,
	featureName,
	disabled,
	anchorIcon = "dots-vertical",
	anchorStyle,
	copyText,
	feedbackDisabled,
	threadId,
	feedbackContext,
	responseId,
}: UseAIFeedbackMenuProps) => {
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [feedbackType, setFeedbackType] = useState<FeedbackType>();
	const [menuVisible, setMenuVisible] = useState(false);
	const [menuMounted, setMenuMounted] = useState(false);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const { isMobile } = useDevice();

	const itemAIFeedback = useAIFeedbackStore(
		(store) => store.aiResponse?.[itemId],
	);
	const sessionId = useSessionStore((state) => state.id);
	const effectiveThreadId = threadId ?? itemAIFeedback?.runDetails?.threadId;

	const negativeReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("negative", t),
		[t],
	);

	const reportReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("report", t),
		[t],
	);

	const handleFeedback = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		(type: FeedbackType) => {
			if (isMobile) {
				router.push({
					pathname: "/feedback",
					params: {
						view: "session",
						itemId: String(itemId),
						featureName,
						feedbackTitle:
							type === "reportIssue" ? t("dialog.report.title") : undefined,
						feedbackSubtitle:
							type === "reportIssue"
								? t("dialog.report.body")
								: type === "negative"
									? t("dialog.feedback.negativeBody")
									: type === "positive"
										? t("dialog.feedback.positiveBody")
										: undefined,
						feedbackIcon: type === "reportIssue" ? "flag-outline" : undefined,
						feedbackCta:
							type === "reportIssue" ? t("dialog.report.cta") : undefined,
						itemFeedbackRating: type === "reportIssue" ? "negative" : type,
						sessionId: String(sessionId),
						sessionType: isSummativeTest ? "SUMMATIVE" : "FORMATIVE",
						threadId: effectiveThreadId,
						runId: itemAIFeedback?.runDetails?.runId,
						context: feedbackContext,
						responseId,
						type: type === "reportIssue" ? "Content Flagged" : undefined,
						feedbackReasonType:
							type === "negative"
								? "negative"
								: type === "reportIssue"
									? "report"
									: undefined,
					},
				});
			} else {
				setFeedbackType(type);
				setShowFeedbackDialog(true);
			}
			setMenuVisible(false);
		},
		[
			itemId,
			effectiveThreadId,
			itemAIFeedback?.runDetails?.runId,
			sessionId,
			t,
			featureName,
			isMobile,
			feedbackContext,
			responseId,
			isSummativeTest,
		],
	);
	const handleDismissFeedback = useCallback(() => {
		setShowFeedbackDialog(false);
	}, []);

	const handleCopy = useCallback(async () => {
		// The Copy item only renders when copyText is set; guard for type safety.
		if (copyText) {
			try {
				await Clipboard.setStringAsync(copyText);
			} catch (error) {
				console.warn("[AIFeedbackMenu] copy to clipboard failed", error);
			}
		}
		setMenuVisible(false);
	}, [copyText]);

	const dialog = getDialogPresentation(
		feedbackType,
		t,
		negativeReasonList,
		reportReasonList,
	);

	const anchorButton = (
		<IconButton
			icon={anchorIcon}
			size={18}
			onPress={() => {
				setMenuMounted(true);
				setMenuVisible(true);
			}}
			disabled={disabled}
			style={[{ margin: 0 }, anchorStyle]}
		/>
	);

	return (
		<>
			{/* Menu mounts lazily: paper's Menu focuses its anchor on mount (web),
			    stealing focus from active inputs when this renders mid-stream */}
			{menuMounted ? (
				<Menu
					visible={menuVisible}
					onDismiss={() => setMenuVisible(false)}
					anchor={anchorButton}
					anchorPosition="bottom"
				>
					{copyText ? (
						<Menu.Item
							leadingIcon="content-copy"
							onPress={handleCopy}
							title={t("report.sessionAnalysis.menuActions.copy")}
						/>
					) : null}
					<Menu.Item
						leadingIcon="thumb-up-outline"
						onPress={() => handleFeedback("positive")}
						title={t("report.sessionAnalysis.menuActions.positiveFeedback")}
						disabled={feedbackDisabled}
					/>
					<Menu.Item
						leadingIcon="thumb-down-outline"
						onPress={() => handleFeedback("negative")}
						title={t("report.sessionAnalysis.menuActions.negativeFeedback")}
						disabled={feedbackDisabled}
					/>
					<Menu.Item
						leadingIcon="flag-outline"
						onPress={() => handleFeedback("reportIssue")}
						title={t("report.sessionAnalysis.menuActions.reportIssue")}
						disabled={feedbackDisabled}
					/>
				</Menu>
			) : (
				anchorButton
			)}
			{/* Same lazy-mount rationale as the Menu: the dialog can only open via a
			    menu item, so transcript rows never pay for an unopened dialog tree. */}
			{menuMounted ? (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={handleDismissFeedback}
					title={dialog.title}
					subtitle={dialog.subtitle}
					icon={dialog.icon}
					cta={dialog.cta}
					itemId={String(itemId)}
					sessionId={sessionId?.toString()}
					runId={itemAIFeedback?.runDetails?.runId}
					rating={dialog.rating}
					type={dialog.type}
					sessionType={isSummativeTest ? "SUMMATIVE" : "FORMATIVE"}
					threadId={effectiveThreadId}
					featureName={featureName}
					context={feedbackContext}
					responseId={responseId}
					feedbackReasonList={dialog.feedbackReasonList}
				/>
			) : null}
		</>
	);
};
